import { useNavigate, useParams, useRouter } from '@tanstack/react-router';

import { CampaignStatus, useCampaignDetail } from '@/entities/campaign';

import { Button } from '@/shared/components/shadcn/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { Stage } from '@/shared/components/stage/stage';
import dayjs from 'dayjs';
import { ArrowLeft } from 'lucide-react';
import { overlay } from 'overlay-kit';
import type { CampaignEditorSection } from '../../campaign-editor/types';
import { CampaignEditDialog } from '../../overlays/campaign-edit-dialog/campaign-edit-dialog';
import { type CampaignDetailTab } from './hooks/campaign-detail-state-types';
import { useCampaignDetailPageState } from './hooks/use-campaign-detail-page-state';
import { CampaignDetailHeader } from './ui/campaign-detail-header';
import { CampaignDetailScheduledState } from './ui/campaign-detail-scheduled-state';
import { CampaignOverviewSkeleton } from './ui/campaign-overview-skeleton';
import { CampaignDetailCreatorTab } from './ui/creator-tab/campaign-detail-creator-tab';
import { CampaignDetailOverviewTab } from './ui/overview-tab/campaign-detail-overview-tab';
import { CampaignDetailPostTab } from './ui/post-tab/campaign-detail-post-tab';

export function CampaignDetailPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const { workspaceId, campaignId } = useParams({
    from: '/workspace/$workspaceId/campaigns/$campaignId/',
  });
  const pageState = useCampaignDetailPageState();
  const campaignDetailQuery = useCampaignDetail(campaignId);
  const campaign = campaignDetailQuery.data ?? null;
  const canEditRequirements = campaign ? campaign.status !== CampaignStatus.ENDED : false;

  const handleBack = () => {
    if (router.history.length > 2) {
      router.history.back();
      return;
    }

    navigate({
      to: '/workspace/$workspaceId/campaigns/',
      params: { workspaceId },
    });
  };

  const handleEditSetup = (initialSection: CampaignEditorSection = 'campaign-info') => {
    if (!canEditRequirements || !campaign) return;
    overlay.open(({ isOpen, close }) => {
      return (
        <CampaignEditDialog
          campaign={campaign}
          campaignId={campaignId}
          initialSection={initialSection}
          isOpen={isOpen}
          onClose={close}
        />
      );
    });
  };

  if (campaignDetailQuery.isError) {
    return (
      <CampaignDetailUnavailable
        title={
          campaignDetailQuery.isError ? 'Campaign details are unavailable' : 'Campaign not found'
        }
        description={
          campaignDetailQuery.isError
            ? 'We could not load this campaign right now. Go back to the list and try again.'
            : 'This campaign could not be found in the current workspace. Go back to the list and choose another campaign.'
        }
        onBack={handleBack}
      />
    );
  }

  return (
    <>
      <Tabs
        value={pageState.activeTab}
        onValueChange={(value) => pageState.setActiveTab(value as CampaignDetailTab)}
        className="flex h-full flex-col gap-0"
      >
        <CampaignDetailHeader
          campaignId={campaignId}
          campaign={campaign}
          onBack={handleBack}
          onEditSetup={() => handleEditSetup('campaign-info')}
          isLoading={campaignDetailQuery.isFetching}
        />

        <main className="relative flex-1 overflow-auto">
          <Stage>
            <Stage.Scene status={campaignDetailQuery.isFetching}>
              <CampaignOverviewSkeleton />
            </Stage.Scene>
            <Stage.Scene default data={campaign}>
              {(campaign) => {
                const isPreScrapped =
                  campaign.status === CampaignStatus.SCHEDULED ||
                  dayjs(campaign.start_date).isSame(dayjs(), 'date');
                return (
                  <Stage>
                    <Stage.Scene status={isPreScrapped}>
                      <CampaignDetailScheduledState scheduledStartDate={campaign.start_date} />
                    </Stage.Scene>
                    <Stage.Scene default>
                      <div className="space-y-6 p-6">
                        <TabsList className="grid w-full max-w-[420px] grid-cols-3">
                          <TabsTrigger value="overview">Overview</TabsTrigger>
                          <TabsTrigger value="creator">Creator</TabsTrigger>
                          <TabsTrigger value="post">Post</TabsTrigger>
                        </TabsList>

                        <TabsContent value="overview" className="mt-0 flex-none">
                          <CampaignDetailOverviewTab
                            campaignId={campaignId}
                            onJumpToAllPosts={() => {
                              pageState.openPostTab('all');
                            }}
                            onJumpToMissingCreators={() => {
                              pageState.openCreatorTab('pending');
                            }}
                            onJumpToRecentPosts={() => {
                              pageState.openPostTab('recent');
                            }}
                          />
                        </TabsContent>

                        <TabsContent value="creator" className="mt-0 flex-none">
                          <CampaignDetailCreatorTab
                            campaignId={campaignId}
                            canEdit={canEditRequirements}
                            filterRequest={pageState.creatorTabFilterRequest}
                            onOpenRequirementEditor={() => handleEditSetup('creators')}
                          />
                        </TabsContent>

                        <TabsContent value="post" className="mt-0 flex-none">
                          <CampaignDetailPostTab
                            campaignId={campaignId}
                            campaign={campaign}
                            filterRequest={pageState.postTabFilterRequest}
                            isActive={pageState.activeTab === 'post'}
                            onOpenRequirementEditor={() => handleEditSetup('post-urls')}
                          />
                        </TabsContent>
                      </div>
                    </Stage.Scene>
                  </Stage>
                );
              }}
            </Stage.Scene>
          </Stage>
        </main>
      </Tabs>
    </>
  );
}

export function CampaignDetailUnavailable({
  title,
  description,
  onBack,
}: {
  title: string;
  description: string;
  onBack: () => void;
}) {
  return (
    <div className="bg-muted flex h-full items-center justify-center px-6 py-12">
      <div className="bg-card border-border w-full max-w-lg rounded-xl border p-8">
        <p className="text-muted-foreground text-xs font-semibold tracking-[0.18em] uppercase">
          Campaign detail
        </p>
        <h1 className="text-foreground mt-3 text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-3 text-sm leading-6">{description}</p>
        <Button className="mt-6" onClick={onBack}>
          <ArrowLeft className="size-4" />
          Back to campaigns
        </Button>
      </div>
    </div>
  );
}
